import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { ITenantListFilter } from '@overa/shared';
import { SecurityStateManagementService, Tenant, TenantService } from '@overa/security';

import { PlatformService } from './platform.service';

/**
 * The BackOffice's tenant service: every tenant selector lists the tenants of the platform being operated.
 *
 * Inside a platform (`/p/{code}`) the list comes from `GET {identity}/Platforms/{code}/tenants`, so a
 * selector never offers another platform's tenants — nor the console's own "main". Outside any platform it
 * is the library's list, restricted to console tenants (those without platform).
 *
 * Writes are the library's, with two differences inside a platform:
 * - a new tenant is created **in that platform**: the library does not send one, and Identity would
 *   otherwise create it where the session tenant lives, the console;
 * - after a write, the change event carries the platform's tenants. The library publishes its own cache,
 *   which holds every platform's tenants.
 */
@Injectable()
export class PlatformTenantService extends TenantService {
  private readonly platforms = inject(PlatformService);

  override getTenants(filter?: ITenantListFilter): Observable<Tenant[]> {
    const code = this.platforms.active()?.code;

    if (!code) {
      return super.getTenants(filter).pipe(map((tenants) => (tenants ?? []).filter((t: any) => !t.platform)));
    }

    return this.platforms.loadTenants(code).pipe(
      map(() =>
        this.platformTenants(code).filter((t: any) => filter?.isEnabled == null || t.isEnabled === filter.isEnabled),
      ),
    );
  }

  override addTenant(tenant: Tenant): Observable<Tenant> {
    const code = this.platforms.active()?.code;
    if (code) {
      (tenant as any).platform = code;
    }
    return this.republish(super.addTenant(tenant), code);
  }

  override editTenant(tenant: Tenant): Observable<Tenant> {
    return this.republish(super.editTenant(tenant), this.platforms.active()?.code);
  }

  override deleteTenant(tenant: Tenant): Observable<Tenant> {
    return this.republish(super.deleteTenant(tenant), this.platforms.active()?.code);
  }

  /** Reloads the platform's tenants after a write and publishes them in place of the library's cache. */
  private republish(operation: Observable<Tenant>, code: string | undefined): Observable<Tenant> {
    if (!code) return operation;

    return operation.pipe(
      switchMap((result) =>
        this.platforms.loadTenants(code, true).pipe(
          tap(() =>
            this.SSMService.onTenantsChanged(
              this.platformTenants(code),
              SecurityStateManagementService.entityModifiedOperation,
            ),
          ),
          map(() => result),
        ),
      ),
    );
  }

  private platformTenants(code: string): Tenant[] {
    return this.platforms.tenants().map(
      (t) =>
        ({
          id: t.id,
          canonicalName: t.canonicalName,
          displayName: t.displayName || t.canonicalName,
          isEnabled: t.isEnabled,
          platform: code,
        }) as unknown as Tenant,
    );
  }
}
